/**
 * Sistema de Logging Condicional para Competition Dashboard
 * 
 * Proporciona logging inteligente que se deshabilita automáticamente en producción
 * para mejorar el rendimiento y la seguridad.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  data?: any[];
}

class Logger {
  private isDevelopment: boolean;
  private logHistory: LogEntry[] = [];
  private maxHistorySize = 100;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
  }

  private createLogEntry(level: LogLevel, message: string, data?: any[]): LogEntry {
    return {
      level,
      message,
      timestamp: new Date().toISOString(),
      data
    };
  }

  private addToHistory(entry: LogEntry): void {
    this.logHistory.push(entry);
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }
  }

  /**
   * Log de debug - Solo se muestra en desarrollo
   */
  debug(message: string, ...data: any[]): void {
    const entry = this.createLogEntry('debug', message, data);
    this.addToHistory(entry);
    
    if (this.isDevelopment) {
      console.log(`🔍 ${message}`, ...data);
    }
  }

  /**
   * Log de información - Solo se muestra en desarrollo
   */
  info(message: string, ...data: any[]): void {
    const entry = this.createLogEntry('info', message, data);
    this.addToHistory(entry);
    
    if (this.isDevelopment) {
      console.info(`ℹ️ ${message}`, ...data);
    }
  }

  /**
   * Log de advertencia - Se muestra siempre pero filtrado en producción
   */
  warn(message: string, ...data: any[]): void {
    const entry = this.createLogEntry('warn', message, data);
    this.addToHistory(entry);
    
    if (this.isDevelopment) {
      console.warn(`⚠️ ${message}`, ...data);
    } else {
      // En producción, solo mostrar warnings críticos
      if (message.includes('Error') || message.includes('Fail')) {
        console.warn(`⚠️ ${message}`);
      }
    }
  }

  /**
   * Log de error - Se muestra siempre
   */
  error(message: string, ...data: any[]): void {
    const entry = this.createLogEntry('error', message, data);
    this.addToHistory(entry);
    
    console.error(`❌ ${message}`, ...data);
  }

  /**
   * Log específico para eventos de timer
   */
  timer(message: string, ...data: any[]): void {
    this.debug(`⏱️ Timer: ${message}`, ...data);
  }

  /**
   * Log específico para configuración
   */
  config(message: string, ...data: any[]): void {
    this.debug(`⚙️ Config: ${message}`, ...data);
  }

  /**
   * Log específico para comunicación entre ventanas
   */
  socket(message: string, ...data: any[]): void {
    this.debug(`🔌 Socket: ${message}`, ...data);
  }

  /**
   * Log específico para bracket
   */
  bracket(message: string, ...data: any[]): void {
    this.debug(`🏆 Bracket: ${message}`, ...data);
  }

  /**
   * Log específico para carousel
   */
  carousel(message: string, ...data: any[]): void {
    this.debug(`🎬 Carousel: ${message}`, ...data);
  }

  /**
   * Log específico para almacenamiento
   */
  storage(message: string, ...data: any[]): void {
    this.debug(`💾 Storage: ${message}`, ...data);
  }

  /**
   * Obtener historial de logs (útil para debugging)
   */
  getHistory(level?: LogLevel): LogEntry[] {
    if (level) {
      return this.logHistory.filter(entry => entry.level === level);
    }
    return [...this.logHistory];
  }

  /**
   * Limpiar historial de logs
   */
  clearHistory(): void {
    this.logHistory = [];
    this.debug('Historial de logs limpiado');
  }

  /**
   * Exportar logs para análisis
   */
  exportLogs(): string {
    const exportData = {
      exportDate: new Date().toISOString(),
      environment: this.isDevelopment ? 'development' : 'production',
      logs: this.logHistory
    };
    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Verificar si el logging está habilitado para un nivel específico
   */
  isLevelEnabled(level: LogLevel): boolean {
    if (level === 'error') return true;
    if (level === 'warn') return true;
    return this.isDevelopment;
  }
}

// Crear instancia singleton del logger
export const logger = new Logger();

// Funciones de conveniencia para mantener compatibilidad
export const logTimer = (message: string, ...data: any[]) => logger.timer(message, ...data);
export const logConfig = (message: string, ...data: any[]) => logger.config(message, ...data);
export const logSocket = (message: string, ...data: any[]) => logger.socket(message, ...data);
export const logBracket = (message: string, ...data: any[]) => logger.bracket(message, ...data);
export const logCarousel = (message: string, ...data: any[]) => logger.carousel(message, ...data);
export const logStorage = (message: string, ...data: any[]) => logger.storage(message, ...data);

// Export default para uso directo
export default logger;
